import { useState, useEffect, useCallback } from "react";
import { collection, query, orderBy, getDocs, addDoc, doc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { computeHash } from "../utils/hashIntegrity";
import { logAudit } from "../utils/auditTrail";
import { sanitizeObject } from "../utils/sanitize";

export default function useClinicalRecords(patientId) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRecords = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, "patients", patientId, "clinical_records"),
        orderBy("fecha", "desc")
      );
      const snap = await getDocs(q);
      setRecords(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error loading records:", err);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const addRecord = useCallback(async (data, user) => {
    if (!patientId) throw new Error("No patientId");
    const existingSnap = await getDocs(collection(db, "patients", patientId, "clinical_records"));
    const consecutivo = existingSnap.size + 1;
    const hashIntegridad = await computeHash(JSON.stringify(data));
    const record = {
      ...sanitizeObject(data),
      consecutivo,
      fecha: serverTimestamp(),
      doctorId: user.uid,
      doctorNombre: user.displayName || user.email,
      hashIntegridad,
      firmaDigital: {
        doctorId: user.uid,
        doctorNombre: user.displayName || user.email,
        registroMedico: user.registroMedico || "",
        timestamp: new Date().toISOString(),
        aceptoResponsabilidad: true,
      },
    };
    const ref = await addDoc(collection(db, "patients", patientId, "clinical_records"), record);
    const snap = await getDoc(ref);
    const newRec = { id: snap.id, ...snap.data() };
    setRecords(prev => [newRec, ...prev]);
    await logAudit("crear_evolucion", "clinical_record", snap.id, user.uid, user.email || "", `Paciente: ${patientId}, #${consecutivo}`, null, hashIntegridad);
    return newRec;
  }, [patientId]);

  const getRecord = useCallback(async (id) => {
    if (!patientId) return null;
    const snap = await getDoc(doc(db, "patients", patientId, "clinical_records", id));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }, [patientId]);

  return { records, loading, addRecord, getRecord, reload: loadRecords };
}
